import type React from 'react';

/**
 * Removes extraneous leading zeros from a numeric input string while
 * preserving a single '0' before a decimal point.
 */
export const stripLeadingZeros = (value: string): string => {
  if (value.length <= 1) return value;

  if (value[0] === '0' && value[1] === '.') {
    return value;
  }

  if (value.startsWith('0') && value.includes('.')) {
    return value.replace(/^0+/, '0');
  }

  const stripped = value.replace(/^0+/, '');
  return stripped || '0';
};

/**
 * Blur handler factory for Mantine `<NumberInput>` that strips non-numeric
 * characters, removes leading zeros, and forwards the cleaned numeric
 * value (or 0 for invalid input) to the provided callback.
 */
export const createNumberInputBlurHandler = (onCleanedValue: (value: number) => void) => {
  return (event: React.FocusEvent<HTMLInputElement>) => {
    const rawValue = event.currentTarget.value;
    const numericOnly = rawValue.replace(/[^\d.-]/g, '');
    const cleaned = stripLeadingZeros(numericOnly);
    const numValue = parseFloat(cleaned);

    onCleanedValue(isNaN(numValue) ? 0 : numValue);
  };
};
